/**
 * Option stack attached to an interactive agent message
 * (`content.kind === 'interactive'`) — the buttons of a `send_message` or
 * the rows of a `send_list`, both flattened to the same shape by
 * `widget/interactive.ts`. Rendered below the agent bubble in the message
 * list.
 *
 * Visual: reuses the "list" style from `QuickReplies` (stacked pills
 * with brand-colored text on the bubble bg). Kept as its own component
 * so the click semantics differ cleanly:
 *   - QuickReplies is a one-time greeting-time affordance driven by
 *     the widget's static `branding.quick_replies` config.
 *   - MessageReplyButtons is per-message and driven by the flow
 *     engine's runtime `send_message`-with-buttons payload; clicking
 *     sends the button's `title` as a normal user message so the
 *     flow's `menu` / `wait_for_reply` compares against the visible
 *     text (matches WhatsApp semantics).
 */

import { Fragment } from 'preact';
import { cn } from '../lib/cn';
import type { ReplyButtonOption } from './useChatState';

interface MessageReplyButtonsProps {
  buttons: ReplyButtonOption[];
  brandingColor: string;
  onSelect: (title: string) => void;
}

/**
 * Renders the stack of reply buttons. Deliberately layout-thin — no
 * outer flex, no `max-w`, no `px-*`. Caller is responsible for placing
 * the component inside the same `max-w-[76%]` wrapper that holds the
 * agent bubble it belongs to. That way the buttons share the exact
 * rendered width of the bubble (WhatsApp's visual model) — a previous
 * revision here had its own `max-w-[76%]` wrapper and blew up in
 * standalone / wide-viewport mode, where the buttons stretched to 76%
 * of the viewport while the sibling bubble stayed content-sized.
 */
export function MessageReplyButtons({
  buttons,
  brandingColor,
  onSelect,
}: MessageReplyButtonsProps) {
  if (!buttons.length) return null;

  return (
    <div class="flex flex-col gap-[4px]">
      {buttons.map((btn, i) => (
        <Fragment key={btn.id}>
          {/* Section heading — only present when a list actually splits its
              rows across more than one titled section (see
              `widget/interactive.ts`), so the common single-section case
              renders as a plain stack. */}
          {btn.sectionTitle && (
            <div
              class={cn(
                'px-1 text-[10.5px] font-semibold uppercase tracking-[0.08em]',
                'text-w-secondary',
                i > 0 ? 'mt-[6px]' : '',
              )}
            >
              {btn.sectionTitle}
            </div>
          )}
          <button
            type="button"
            onClick={() => onSelect(btn.title)}
            style={
              {
                animationDelay: `${120 + 70 * i}ms`,
                '--mirlo-brand': brandingColor,
              } as Record<string, string>
            }
            class={cn(
              'animate-chip-in',
              // 10px radius reads as "WhatsApp button under a bubble"
              // (rounded rectangle) instead of the 13px pill it used to be.
              'w-full rounded-[10px] px-4 py-[8px]',
              'bg-w-bb-bg text-[var(--brand)] text-center',
              'text-[13.5px] font-semibold',
              'shadow-[0_1px_1px_rgba(0,0,0,0.08)]',
              'border-0 cursor-pointer',
              'hover:opacity-80 active:scale-[0.98] transition-all duration-150',
            )}
          >
            {btn.title}
            {/* List rows may carry a secondary line; buttons never do. */}
            {btn.description && (
              <span class="block text-[11.5px] font-normal text-w-secondary mt-[1px]">
                {btn.description}
              </span>
            )}
          </button>
        </Fragment>
      ))}
    </div>
  );
}
